/**
 * @module defaultAdapters
 * @package @geenius/adapters/react-native
 * @description Provides React Native-safe default adapter implementations.
 * They use an AsyncStorage-compatible key/value store when one is supplied and
 * fall back to an in-process Map for tests, previews, and offline prototypes.
 */

import type * as AdapterContracts from "@geenius/adapters";
import {
  AdminError,
  AiError,
  AuthError,
  createConsoleLoggerAdapter,
  DbError,
  PaymentsError,
  StorageError,
} from "@geenius/adapters";

type AdapterDomain = AdapterContracts.AdapterDomain;
type AdminAdapter = AdapterContracts.AdminAdapter;
type AdminListOptions = AdapterContracts.AdminListOptions;
type AdminMetrics = AdapterContracts.AdminMetrics;
type AdminRole = AdapterContracts.AdminRole;
type AdminRoleInput = AdapterContracts.AdminRoleInput;
type AdminTeam = AdapterContracts.AdminTeam;
type AdminTeamInput = AdapterContracts.AdminTeamInput;
type AiAdapter = AdapterContracts.AiAdapter;
type AiOptions = AdapterContracts.AiOptions;
type AuthAdapter = AdapterContracts.AuthAdapter;
type AuthSession = AdapterContracts.AuthSession;
type AuthUser = AdapterContracts.AuthUser;
type ChatMessage = AdapterContracts.ChatMessage;
type ChatResponse = AdapterContracts.ChatResponse;
type CheckoutParams = AdapterContracts.CheckoutParams;
type DbAdapter = AdapterContracts.DbAdapter;
type FileStorageAdapter = AdapterContracts.FileStorageAdapter;
type ListOptions = AdapterContracts.ListOptions;
type LoggerAdapter = AdapterContracts.LoggerAdapter;
type ManagedUser = AdapterContracts.ManagedUser;
type OAuthProvider = AdapterContracts.OAuthProvider;
type PaymentsAdapter = AdapterContracts.PaymentsAdapter;
type Plan = AdapterContracts.Plan;
type QueryCondition = AdapterContracts.QueryCondition;
type QueryFilter = AdapterContracts.QueryFilter;
type StoredFile = AdapterContracts.StoredFile;
type Subscription = AdapterContracts.Subscription;

export interface ReactNativeAsyncStorage {
  getItem(key: string): Promise<string | null> | string | null;
  removeItem(key: string): Promise<void> | void;
  setItem(key: string, value: string): Promise<void> | void;
}

/**
 * Controls deterministic rate-limit simulation for the React Native AI mock.
 */
export interface ReactNativeAiRateLimitOptions {
  /** Number of successful AI operations allowed before RATE_LIMIT is thrown. */
  afterRequests?: number;
  /** Error message used when the configured limit is reached. */
  message?: string;
}

export interface ReactNativeDefaultAdapterOptions {
  asyncStorage?: ReactNativeAsyncStorage;
  isProduction?: boolean;
  maxUploadBytes?: number;
  namespace?: string;
  /** Enables deterministic RATE_LIMIT failures on the AI mock for tests and demos. */
  aiRateLimit?: boolean | ReactNativeAiRateLimitOptions;
}

export interface ReactNativeDefaultAdapterSet {
  adapters: {
    admin: AdminAdapter;
    ai: AiAdapter;
    auth: AuthAdapter;
    db: DbAdapter;
    fileStorage: FileStorageAdapter;
    logger: LoggerAdapter;
    payments: PaymentsAdapter;
    storage: FileStorageAdapter;
  };
  providerName: string;
}

type StoredRecord = Record<string, unknown> & { id: string };
type StoredNativeUser = AuthUser & {
  email: string;
  isBanned?: boolean;
  lastLoginAt?: string;
  passwordHash: string;
  plan?: string;
  roleIds?: string[];
  teamIds?: string[];
};
type StoredNativeAdminRole = AdminRole;
type StoredNativeAdminTeam = AdminTeam;
type StoredNativeFile = StoredFile & {
  blobKey: string;
};
type StoredHistoryEntry = {
  messages: ChatMessage[];
  response: string;
  timestamp: string;
};

const BASE64_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

const DEFAULT_PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    price: 0,
    currency: "USD",
    interval: "month",
    features: ["basic features", "1 project", "community support"],
  },
  {
    id: "pro",
    name: "Pro",
    price: 29,
    currency: "USD",
    interval: "month",
    features: ["ai features", "unlimited projects", "priority support"],
  },
  {
    id: "team",
    name: "Team",
    price: 79,
    currency: "USD",
    interval: "month",
    features: ["team management", "admin dashboard", "custom integrations"],
  },
];

const CANNED_RESPONSES = [
  "This React Native mock adapter is ready for local prototyping.",
  "The adapter contract is active. Replace this mock with a production provider when you configure your app.",
  "React Native can use the same adapter contract without importing browser APIs.",
  "This response is deterministic and local-first for mobile previews.",
];

const DEFAULT_MAX_UPLOAD_BYTES = 512 * 1024;

function isProductionRuntime(isProduction?: boolean): boolean {
  if (typeof isProduction === "boolean") {
    return isProduction;
  }
  const runtime = globalThis as {
    process?: { env?: { NODE_ENV?: string } };
  };
  return runtime.process?.env?.NODE_ENV === "production";
}

function createMemoryStorage(): ReactNativeAsyncStorage {
  const entries = new Map<string, string>();

  return {
    getItem(key) {
      return entries.get(key) ?? null;
    },
    removeItem(key) {
      entries.delete(key);
    },
    setItem(key, value) {
      entries.set(key, value);
    },
  };
}

function storageKey(namespace: string, key: string): string {
  return `${namespace}:${key}`;
}

function createId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function isStoredRecord(value: unknown): value is StoredRecord {
  return isRecord(value) && typeof value.id === "string";
}

function isChatMessage(value: unknown): value is ChatMessage {
  return (
    isRecord(value) &&
    ["system", "user", "assistant"].includes(String(value.role)) &&
    typeof value.content === "string"
  );
}

function isStoredHistoryEntry(value: unknown): value is StoredHistoryEntry {
  return (
    isRecord(value) &&
    typeof value.timestamp === "string" &&
    typeof value.response === "string" &&
    Array.isArray(value.messages) &&
    value.messages.every(isChatMessage)
  );
}

function isStoredNativeUser(value: unknown): value is StoredNativeUser {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.email === "string" &&
    typeof value.passwordHash === "string" &&
    (value.name === undefined || typeof value.name === "string") &&
    (value.image === undefined || typeof value.image === "string") &&
    (value.role === undefined || typeof value.role === "string") &&
    (value.createdAt === undefined || typeof value.createdAt === "string") &&
    (value.isBanned === undefined || typeof value.isBanned === "boolean") &&
    (value.plan === undefined || typeof value.plan === "string") &&
    (value.lastLoginAt === undefined ||
      typeof value.lastLoginAt === "string") &&
    (value.roleIds === undefined || isStringArray(value.roleIds)) &&
    (value.teamIds === undefined || isStringArray(value.teamIds))
  );
}

function isStoredNativeAdminRole(
  value: unknown,
): value is StoredNativeAdminRole {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    (value.description === undefined ||
      typeof value.description === "string") &&
    isStringArray(value.permissions) &&
    (value.isSystem === undefined || typeof value.isSystem === "boolean") &&
    (value.createdAt === undefined || typeof value.createdAt === "string") &&
    (value.updatedAt === undefined || typeof value.updatedAt === "string")
  );
}

function isStoredNativeAdminTeam(
  value: unknown,
): value is StoredNativeAdminTeam {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    (value.description === undefined ||
      typeof value.description === "string") &&
    isStringArray(value.memberUserIds) &&
    isStringArray(value.roleIds) &&
    (value.createdAt === undefined || typeof value.createdAt === "string") &&
    (value.updatedAt === undefined || typeof value.updatedAt === "string")
  );
}

function isAuthSession(value: unknown): value is AuthSession {
  return (
    isRecord(value) &&
    typeof value.userId === "string" &&
    (value.sessionToken === undefined ||
      typeof value.sessionToken === "string") &&
    (value.expiresAt === undefined || typeof value.expiresAt === "string")
  );
}

function isStoredNativeFile(value: unknown): value is StoredNativeFile {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    typeof value.path === "string" &&
    typeof value.size === "number" &&
    typeof value.mimeType === "string" &&
    typeof value.url === "string" &&
    typeof value.createdAt === "string" &&
    typeof value.blobKey === "string"
  );
}

function isPlan(value: unknown): value is Plan {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    typeof value.price === "number" &&
    typeof value.currency === "string" &&
    ["month", "year", "one-time"].includes(String(value.interval)) &&
    Array.isArray(value.features) &&
    value.features.every((feature) => typeof feature === "string")
  );
}

function isSubscription(value: unknown): value is Subscription {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.userId === "string" &&
    typeof value.planId === "string" &&
    ["active", "cancelled", "past_due", "trialing"].includes(
      String(value.status),
    ) &&
    typeof value.currentPeriodEnd === "string" &&
    typeof value.cancelAtPeriodEnd === "boolean"
  );
}

async function readJsonArray<T>(
  storage: ReactNativeAsyncStorage,
  key: string,
  guard: (value: unknown) => value is T,
  error: (cause: unknown) => Error,
): Promise<T[]> {
  try {
    const raw = await storage.getItem(key);
    const parsed: unknown = JSON.parse(raw ?? "[]");
    if (!Array.isArray(parsed) || !parsed.every(guard)) {
      throw error(undefined);
    }
    return parsed;
  } catch (cause) {
    throw error(cause);
  }
}

async function writeJson(
  storage: ReactNativeAsyncStorage,
  key: string,
  value: unknown,
  error: (cause: unknown) => Error,
): Promise<void> {
  try {
    await storage.setItem(key, JSON.stringify(value));
  } catch (cause) {
    throw error(cause);
  }
}

function compareValues(
  left: unknown,
  right: unknown,
  operator: Extract<QueryCondition["operator"], "gt" | "gte" | "lt" | "lte">,
): boolean {
  if (typeof left === "number" && typeof right === "number") {
    if (operator === "gt") return left > right;
    if (operator === "gte") return left >= right;
    if (operator === "lt") return left < right;
    return left <= right;
  }

  if (typeof left === "string" && typeof right === "string") {
    if (operator === "gt") return left > right;
    if (operator === "gte") return left >= right;
    if (operator === "lt") return left < right;
    return left <= right;
  }

  return false;
}

function matchesCondition(
  item: StoredRecord,
  condition: QueryCondition,
): boolean {
  const value = item[condition.field];

  switch (condition.operator) {
    case "eq":
      return value === condition.value;
    case "neq":
      return value !== condition.value;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return compareValues(value, condition.value, condition.operator);
    case "in":
      return Array.isArray(condition.value) && condition.value.includes(value);
    case "contains":
      return (
        typeof value === "string" &&
        typeof condition.value === "string" &&
        value.includes(condition.value)
      );
    default:
      return false;
  }
}

function matchesFilter(item: StoredRecord, filter: QueryFilter): boolean {
  return filter.every((condition: QueryCondition) =>
    matchesCondition(item, condition),
  );
}

function castRecord<T>(record: StoredRecord): T {
  return record as unknown as T;
}

function castRecords<T>(records: StoredRecord[]): T[] {
  return records as unknown as T[];
}

function simpleHash(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function publicUser(user: StoredNativeUser): AuthUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    image: user.image,
    role: user.role,
    createdAt: user.createdAt,
  };
}

function managedUser(user: StoredNativeUser): ManagedUser {
  return {
    ...publicUser(user),
    isActive: true,
    isBanned: user.isBanned ?? false,
    roleIds: uniqueStrings(user.roleIds ?? []),
    teamIds: uniqueStrings(user.teamIds ?? []),
    plan: user.plan ?? "free",
    lastLoginAt: user.lastLoginAt,
  };
}

function uniqueStrings(items: readonly string[]): string[] {
  return [...new Set(items)];
}

function applyAdminListOptions<T>(
  items: T[],
  options: AdminListOptions | undefined,
  getSearchText: (item: T) => Array<string | undefined>,
): T[] {
  let filtered = items;

  if (options?.search) {
    const query = options.search.toLowerCase();
    filtered = filtered.filter((item) =>
      getSearchText(item).some((value) => value?.toLowerCase().includes(query)),
    );
  }

  const offset = options?.offset ?? 0;
  const limit = options?.limit ?? 50;
  return filtered.slice(offset, offset + limit);
}

function makeNativeAdminRole(
  input: AdminRoleInput,
  existing?: AdminRole,
): AdminRole {
  const now = new Date().toISOString();
  return {
    id: input.id ?? existing?.id ?? createId("role"),
    name: input.name,
    description: input.description,
    permissions: uniqueStrings(
      input.permissions ?? existing?.permissions ?? [],
    ),
    isSystem: input.isSystem ?? existing?.isSystem,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
}

function makeNativeAdminTeam(
  input: AdminTeamInput,
  existing?: AdminTeam,
): AdminTeam {
  const now = new Date().toISOString();
  return {
    id: input.id ?? existing?.id ?? createId("team"),
    name: input.name,
    description: input.description,
    memberUserIds: uniqueStrings(
      input.memberUserIds ?? existing?.memberUserIds ?? [],
    ),
    roleIds: uniqueStrings(input.roleIds ?? existing?.roleIds ?? []),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
}

function upsertById<T extends { id: string }>(items: T[], item: T): T[] {
  const index = items.findIndex((candidate) => candidate.id === item.id);
  if (index < 0) {
    return [...items, item];
  }

  const next = [...items];
  next[index] = item;
  return next;
}

function getCannedResponse(messages: ChatMessage[]): string {
  const lastMessage = messages.at(-1)?.content ?? "";
  let hash = 0;
  for (let index = 0; index < lastMessage.length; index += 1) {
    hash = (hash << 5) - hash + lastMessage.charCodeAt(index);
    hash |= 0;
  }
  return (
    CANNED_RESPONSES[Math.abs(hash) % CANNED_RESPONSES.length] ??
    CANNED_RESPONSES[0] ??
    ""
  );
}

function bytesToBase64(bytes: Uint8Array): string {
  let output = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1] ?? 0;
    const third = bytes[index + 2] ?? 0;
    const triplet = (first << 16) | (second << 8) | third;
    output += BASE64_ALPHABET[(triplet >> 18) & 63] ?? "";
    output += BASE64_ALPHABET[(triplet >> 12) & 63] ?? "";
    output +=
      index + 1 < bytes.length
        ? (BASE64_ALPHABET[(triplet >> 6) & 63] ?? "")
        : "=";
    output +=
      index + 2 < bytes.length ? (BASE64_ALPHABET[triplet & 63] ?? "") : "=";
  }
  return output;
}

function base64ToBytes(base64: string): Uint8Array {
  const clean = base64.replace(/=+$/, "");
  const output: number[] = [];

  for (let index = 0; index < clean.length; index += 4) {
    const first = BASE64_ALPHABET.indexOf(clean[index] ?? "A");
    const second = BASE64_ALPHABET.indexOf(clean[index + 1] ?? "A");
    const third = BASE64_ALPHABET.indexOf(clean[index + 2] ?? "A");
    const fourth = BASE64_ALPHABET.indexOf(clean[index + 3] ?? "A");
    const triplet = (first << 18) | (second << 12) | (third << 6) | fourth;
    output.push((triplet >> 16) & 255);
    if (index + 2 < clean.length) output.push((triplet >> 8) & 255);
    if (index + 3 < clean.length) output.push(triplet & 255);
  }

  return new Uint8Array(output);
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  if (typeof blob.arrayBuffer !== "function") {
    throw new StorageError(
      "React Native file storage upload requires Blob.arrayBuffer(); provide a compatible Blob polyfill or a native storage provider adapter.",
      "UPLOAD_FAILED",
    );
  }
  let arrayBuffer: ArrayBuffer;
  try {
    arrayBuffer = await blob.arrayBuffer();
  } catch (cause) {
    throw new StorageError(
      "Failed to read React Native upload blob",
      "UPLOAD_FAILED",
      cause,
    );
  }
  const bytes = new Uint8Array(arrayBuffer);
  const mimeType = blob.type || "application/octet-stream";
  return `data:${mimeType};base64,${bytesToBase64(bytes)}`;
}

function dataUrlToBlob(dataUrl: string): Blob {
  if (typeof Blob !== "function") {
    throw new StorageError(
      "React Native file storage download requires a Blob constructor; provide a compatible Blob polyfill or a native storage provider adapter.",
      "DOWNLOAD_FAILED",
    );
  }
  const [meta = "", payload = ""] = dataUrl.split(",");
  const mimeType =
    meta.match(/data:(.*);base64/)?.[1] ?? "application/octet-stream";
  const bytes = base64ToBytes(payload);
  const arrayBuffer = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
  return new Blob([arrayBuffer], { type: mimeType });
}

function createNativeDbAdapter(
  storage: ReactNativeAsyncStorage,
  namespace: string,
): DbAdapter {
  const collectionKey = (collection: string) =>
    storageKey(namespace, `db:${collection}`);
  const readCollection = (collection: string) =>
    readJsonArray(
      storage,
      collectionKey(collection),
      isStoredRecord,
      (cause) =>
        new DbError(
          "Failed to read React Native DB data",
          "QUERY_ERROR",
          cause,
        ),
    );
  const writeCollection = (collection: string, records: StoredRecord[]) =>
    writeJson(
      storage,
      collectionKey(collection),
      records,
      (cause) =>
        new DbError(
          "Failed to write React Native DB data",
          "QUERY_ERROR",
          cause,
        ),
    );

  return {
    async create<T extends Record<string, unknown>>(
      collection: string,
      data: Omit<T, "id">,
    ) {
      const records = await readCollection(collection);
      const record: StoredRecord = { ...data, id: createId("record") };
      records.push(record);
      await writeCollection(collection, records);
      return castRecord<T & { id: string }>(record);
    },
    async get<T>(collection: string, id: string) {
      const record = (await readCollection(collection)).find(
        (candidate) => candidate.id === id,
      );
      return record ? castRecord<T>(record) : null;
    },
    async update<T extends Record<string, unknown>>(
      collection: string,
      id: string,
      data: Partial<T>,
    ) {
      const records = await readCollection(collection);
      const index = records.findIndex((candidate) => candidate.id === id);
      if (index < 0) return null;
      const record = records[index];
      if (!record) return null;
      records[index] = { ...record, ...data };
      await writeCollection(collection, records);
      return castRecord<T>(records[index] ?? record);
    },
    async delete(collection: string, id: string) {
      const records = await readCollection(collection);
      const nextRecords = records.filter((candidate) => candidate.id !== id);
      if (nextRecords.length === records.length) return false;
      await writeCollection(collection, nextRecords);
      return true;
    },
    async list<T>(collection: string, options?: ListOptions) {
      let records = await readCollection(collection);
      if (options?.orderBy) {
        const { orderBy } = options;
        const direction = options.order === "desc" ? -1 : 1;
        records = [...records].sort((left, right) => {
          const leftValue = left[orderBy];
          const rightValue = right[orderBy];
          if (typeof leftValue === "number" && typeof rightValue === "number") {
            return leftValue < rightValue
              ? -direction
              : leftValue > rightValue
                ? direction
                : 0;
          }
          if (typeof leftValue === "string" && typeof rightValue === "string") {
            return leftValue < rightValue
              ? -direction
              : leftValue > rightValue
                ? direction
                : 0;
          }
          return 0;
        });
      }
      if (options?.offset) records = records.slice(options.offset);
      if (options?.limit) records = records.slice(0, options.limit);
      return castRecords<T>(records);
    },
    async query<T>(collection: string, filter: QueryFilter) {
      return castRecords<T>(
        (await readCollection(collection)).filter((record) =>
          matchesFilter(record, filter),
        ),
      );
    },
    async count(collection: string, filter?: QueryFilter) {
      const records = await readCollection(collection);
      return filter && filter.length > 0
        ? records.filter((record) => matchesFilter(record, filter)).length
        : records.length;
    },
  } satisfies DbAdapter;
}

function createNativeAuthAdapter(
  storage: ReactNativeAsyncStorage,
  namespace: string,
): AuthAdapter {
  const usersKey = storageKey(namespace, "auth:users");
  const sessionKey = storageKey(namespace, "auth:session");
  const readUsers = () =>
    readJsonArray(
      storage,
      usersKey,
      isStoredNativeUser,
      (cause) =>
        new AuthError(
          "Failed to read React Native auth users",
          "SERVICE_ERROR",
          cause,
        ),
    );
  const writeUsers = (users: StoredNativeUser[]) =>
    writeJson(
      storage,
      usersKey,
      users,
      (cause) =>
        new AuthError(
          "Failed to write React Native auth users",
          "SERVICE_ERROR",
          cause,
        ),
    );

  async function createSession(user: StoredNativeUser): Promise<AuthSession> {
    user.lastLoginAt = new Date().toISOString();
    const session: AuthSession = {
      userId: user.id,
      sessionToken: createId("session"),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      user: publicUser(user),
    };
    try {
      await storage.setItem(sessionKey, JSON.stringify(session));
    } catch (cause) {
      throw new AuthError(
        "Failed to write React Native auth session",
        "SERVICE_ERROR",
        cause,
      );
    }
    return session;
  }

  return {
    async signIn(email: string, password: string) {
      const users = await readUsers();
      const user = users.find(
        (candidate) => candidate.email.toLowerCase() === email.toLowerCase(),
      );
      if (!user || user.passwordHash !== simpleHash(password)) {
        throw new AuthError("Invalid email or password", "INVALID_CREDENTIALS");
      }
      const session = await createSession(user);
      await writeUsers(users);
      return session;
    },
    async signUp(email: string, password: string, name?: string) {
      const users = await readUsers();
      if (
        users.some(
          (candidate) => candidate.email.toLowerCase() === email.toLowerCase(),
        )
      ) {
        throw new AuthError("User already exists", "USER_EXISTS");
      }
      const user: StoredNativeUser = {
        id: createId("user"),
        email,
        name: name ?? email.split("@")[0],
        createdAt: new Date().toISOString(),
        passwordHash: simpleHash(password),
      };
      users.push(user);
      const session = await createSession(user);
      await writeUsers(users);
      return session;
    },
    async signInWithOAuth(
      provider: OAuthProvider,
      options?: { redirectUrl?: string },
    ) {
      return {
        url:
          options?.redirectUrl ??
          `geenius://oauth/${encodeURIComponent(String(provider))}`,
      };
    },
    async signOut() {
      try {
        await storage.removeItem(sessionKey);
      } catch (cause) {
        throw new AuthError(
          "Failed to clear React Native auth session",
          "SERVICE_ERROR",
          cause,
        );
      }
    },
    async getSession() {
      let raw: string | null;
      try {
        raw = await storage.getItem(sessionKey);
      } catch (cause) {
        throw new AuthError(
          "Failed to read React Native auth session",
          "SERVICE_ERROR",
          cause,
        );
      }
      if (!raw) return null;
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch (cause) {
        throw new AuthError(
          "Failed to read React Native auth session",
          "SERVICE_ERROR",
          cause,
        );
      }
      if (!isAuthSession(parsed)) {
        throw new AuthError(
          "Invalid React Native session payload",
          "SERVICE_ERROR",
        );
      }
      if (parsed.expiresAt && new Date(parsed.expiresAt) < new Date()) {
        try {
          await storage.removeItem(sessionKey);
        } catch (cause) {
          throw new AuthError(
            "Failed to clear expired React Native auth session",
            "SERVICE_ERROR",
            cause,
          );
        }
        return null;
      }
      return parsed;
    },
    async getUser() {
      const session = await this.getSession();
      if (!session) return null;
      const user = (await readUsers()).find(
        (candidate) => candidate.id === session.userId,
      );
      return user ? publicUser(user) : null;
    },
    async updateUser(updates: Partial<Pick<AuthUser, "name" | "image">>) {
      const session = await this.getSession();
      if (!session) return null;
      const users = await readUsers();
      const index = users.findIndex(
        (candidate) => candidate.id === session.userId,
      );
      if (index < 0) return null;
      const user = users[index];
      if (!user) return null;
      users[index] = { ...user, ...updates };
      await writeUsers(users);
      return publicUser(users[index] ?? user);
    },
  } satisfies AuthAdapter;
}

function createNativePaymentsAdapter(
  storage: ReactNativeAsyncStorage,
  namespace: string,
): PaymentsAdapter {
  const subscriptionsKey = storageKey(namespace, "payments:subscriptions");
  const plansKey = storageKey(namespace, "payments:plans");
  const readSubscriptions = () =>
    readJsonArray(
      storage,
      subscriptionsKey,
      isSubscription,
      (cause) =>
        new PaymentsError(
          "Failed to read React Native subscriptions",
          "SERVICE_ERROR",
          cause,
        ),
    );
  const writeSubscriptions = (subscriptions: Subscription[]) =>
    writeJson(
      storage,
      subscriptionsKey,
      subscriptions,
      (cause) =>
        new PaymentsError(
          "Failed to write React Native subscriptions",
          "SERVICE_ERROR",
          cause,
        ),
    );

  async function readPlans(): Promise<Plan[]> {
    let raw: string | null;
    try {
      raw = await storage.getItem(plansKey);
    } catch (cause) {
      throw new PaymentsError(
        "Failed to read React Native plans",
        "SERVICE_ERROR",
        cause,
      );
    }
    if (!raw) {
      await writeJson(
        storage,
        plansKey,
        DEFAULT_PLANS,
        (cause) =>
          new PaymentsError(
            "Failed to write React Native plans",
            "SERVICE_ERROR",
            cause,
          ),
      );
      return DEFAULT_PLANS;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (cause) {
      throw new PaymentsError(
        "Failed to read React Native plans",
        "SERVICE_ERROR",
        cause,
      );
    }
    if (!Array.isArray(parsed) || !parsed.every(isPlan)) {
      throw new PaymentsError(
        "Invalid React Native plan payload",
        "SERVICE_ERROR",
      );
    }
    return parsed;
  }

  return {
    async createCheckout(params: CheckoutParams) {
      const plan = (await readPlans()).find(
        (candidate) => candidate.id === params.planId,
      );
      if (!plan) {
        throw new PaymentsError(
          `Unknown plan: ${params.planId}`,
          "INVALID_PLAN",
        );
      }
      const subscriptions = await readSubscriptions();
      const subscription: Subscription = {
        id: createId("subscription"),
        userId: params.userId,
        planId: plan.id,
        status: "active",
        currentPeriodEnd: new Date(
          Date.now() + 30 * 24 * 60 * 60 * 1000,
        ).toISOString(),
        cancelAtPeriodEnd: false,
      };
      subscriptions.push(subscription);
      await writeSubscriptions(subscriptions);
      return {
        url: params.successUrl ?? "geenius://checkout/success",
        sessionId: subscription.id,
      };
    },
    async getSubscription(userId: string) {
      return (
        (await readSubscriptions()).find(
          (candidate) =>
            candidate.userId === userId && candidate.status === "active",
        ) ?? null
      );
    },
    async cancelSubscription(subscriptionId: string) {
      const subscriptions = await readSubscriptions();
      const index = subscriptions.findIndex(
        (candidate) => candidate.id === subscriptionId,
      );
      if (index < 0) {
        throw new PaymentsError(
          `Subscription not found: ${subscriptionId}`,
          "SUBSCRIPTION_NOT_FOUND",
        );
      }
      const subscription = subscriptions[index];
      if (!subscription) {
        throw new PaymentsError(
          `Subscription not found: ${subscriptionId}`,
          "SUBSCRIPTION_NOT_FOUND",
        );
      }
      subscriptions[index] = {
        ...subscription,
        status: "cancelled",
        cancelAtPeriodEnd: true,
      };
      await writeSubscriptions(subscriptions);
    },
    getPlans() {
      return readPlans();
    },
    async isFeatureEnabled(userId: string, feature: string) {
      const subscription = await this.getSubscription(userId);
      if (!subscription) return false;
      const plan = (await readPlans()).find(
        (candidate) => candidate.id === subscription.planId,
      );
      const normalizedFeature = feature.toLowerCase();
      return (
        subscription.status === "active" &&
        !!plan?.features.some((candidate: string) =>
          candidate.toLowerCase().includes(normalizedFeature),
        )
      );
    },
  } satisfies PaymentsAdapter;
}

function isNativeChatMessage(value: unknown): value is ChatMessage {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.role === "string" &&
    ["system", "user", "assistant"].includes(record.role) &&
    typeof record.content === "string"
  );
}

function validateNativeChatMessages(messages: unknown): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new AiError("AI messages must be a non-empty array", "INVALID_INPUT");
  }
  if (!messages.every(isNativeChatMessage)) {
    throw new AiError(
      "AI messages must include valid role and content fields",
      "INVALID_INPUT",
    );
  }
  return messages.map((message) => ({
    role: message.role,
    content: message.content,
  }));
}

function validateNativeEmbeddingInput(text: unknown): string[] {
  const inputs = Array.isArray(text) ? text : [text];
  if (
    inputs.length === 0 ||
    !inputs.every((item) => typeof item === "string" && item.length > 0)
  ) {
    throw new AiError(
      "AI embedding input must be a non-empty string or non-empty string array",
      "INVALID_INPUT",
    );
  }
  return inputs;
}

const DEFAULT_NATIVE_AI_RATE_LIMIT_MESSAGE =
  "Local AI mock rate limit exceeded for this test scenario.";

function getNativeRateLimitThreshold(
  rateLimit: boolean | ReactNativeAiRateLimitOptions | undefined,
): number | null {
  if (rateLimit === undefined || rateLimit === false) {
    return null;
  }
  if (rateLimit === true) {
    return 0;
  }
  return Math.max(0, rateLimit.afterRequests ?? 0);
}

function assertWithinNativeRateLimit(
  rateLimit: boolean | ReactNativeAiRateLimitOptions | undefined,
  requestCount: number,
): void {
  const threshold = getNativeRateLimitThreshold(rateLimit);
  if (threshold === null || requestCount < threshold) {
    return;
  }
  const message =
    rateLimit && typeof rateLimit === "object" ? rateLimit.message : undefined;
  throw new AiError(
    message ?? DEFAULT_NATIVE_AI_RATE_LIMIT_MESSAGE,
    "RATE_LIMIT",
  );
}

function createNativeAiAdapter(
  storage: ReactNativeAsyncStorage,
  namespace: string,
  rateLimit?: boolean | ReactNativeAiRateLimitOptions,
): AiAdapter {
  const historyKey = storageKey(namespace, "ai:history");
  let requestCount = 0;
  const readHistory = () =>
    readJsonArray(
      storage,
      historyKey,
      isStoredHistoryEntry,
      (cause) =>
        new AiError(
          "Failed to read React Native AI history",
          "SERVICE_ERROR",
          cause,
        ),
    );

  async function saveHistory(messages: ChatMessage[], response: string) {
    const history = await readHistory();
    history.push({ timestamp: new Date().toISOString(), messages, response });
    await writeJson(
      storage,
      historyKey,
      history.slice(-50),
      (cause) =>
        new AiError(
          "Failed to write React Native AI history",
          "SERVICE_ERROR",
          cause,
        ),
    );
  }

  return {
    async chat(
      messages: ChatMessage[],
      _options?: AiOptions,
    ): Promise<ChatResponse> {
      const validated = validateNativeChatMessages(messages);
      assertWithinNativeRateLimit(rateLimit, requestCount);
      const content = getCannedResponse(validated);
      await saveHistory(validated, content);
      requestCount += 1;
      return {
        content,
        finishReason: "stop",
        usage: { promptTokens: 10, completionTokens: 12, totalTokens: 22 },
      };
    },
    async complete(prompt: string, options?: AiOptions) {
      if (typeof prompt !== "string" || prompt.length === 0) {
        throw new AiError(
          "AI prompt must be a non-empty string",
          "INVALID_INPUT",
        );
      }
      const response = await this.chat(
        [{ role: "user", content: prompt }],
        options,
      );
      return response.content;
    },
    async embed(text: string | string[]) {
      const values = validateNativeEmbeddingInput(text);
      assertWithinNativeRateLimit(rateLimit, requestCount);
      requestCount += 1;
      return values.map((value) => {
        const vector: number[] = [];
        for (let index = 0; index < 8; index += 1) {
          let hash = index * 31;
          for (let char = 0; char < value.length; char += 1) {
            hash = (hash << 5) - hash + value.charCodeAt(char);
            hash |= 0;
          }
          vector.push(Math.sin(hash) * 0.5 + 0.5);
        }
        return vector;
      });
    },
    async stream(messages: ChatMessage[], _options?: AiOptions) {
      const validated = validateNativeChatMessages(messages);
      assertWithinNativeRateLimit(rateLimit, requestCount);
      requestCount += 1;
      async function* generate() {
        const content = getCannedResponse(validated);
        await saveHistory(validated, content);
        for (const token of content.split(" ")) {
          yield `${token} `;
        }
      }
      return generate();
    },
  } satisfies AiAdapter;
}

function createNativeFileStorageAdapter(
  storage: ReactNativeAsyncStorage,
  namespace: string,
  maxUploadBytes: number,
): FileStorageAdapter {
  const filesKey = storageKey(namespace, "storage:files");
  const readFiles = () =>
    readJsonArray(
      storage,
      filesKey,
      isStoredNativeFile,
      (cause) =>
        new StorageError(
          "Failed to read React Native file metadata",
          "SERVICE_ERROR",
          cause,
        ),
    );
  const writeFiles = (files: StoredNativeFile[]) =>
    writeJson(
      storage,
      filesKey,
      files,
      (cause) =>
        new StorageError(
          "Failed to write React Native file metadata",
          "SERVICE_ERROR",
          cause,
        ),
    );
  const removeUploadBlob = async (blobKey: string) => {
    try {
      await storage.removeItem(blobKey);
    } catch {
      // Keep the original metadata failure as the surfaced upload error.
    }
  };

  return {
    async upload(file: File | Blob, path: string, name?: string) {
      if (file.size > maxUploadBytes) {
        throw new StorageError(
          `React Native upload payload exceeds ${maxUploadBytes} bytes`,
          "PAYLOAD_TOO_LARGE",
        );
      }

      const id = createId("file");
      const blobKey = storageKey(namespace, `storage:blob:${id}`);
      const dataUrl = await blobToDataUrl(file);
      try {
        await storage.setItem(blobKey, dataUrl);
      } catch (cause) {
        throw new StorageError(
          "Failed to write React Native file blob",
          "UPLOAD_FAILED",
          cause,
        );
      }
      const storedFile: StoredNativeFile = {
        id,
        name:
          name ??
          (typeof File !== "undefined" && file instanceof File
            ? file.name
            : `file-${id.slice(-8)}`),
        path,
        size: file.size,
        mimeType: file.type || "application/octet-stream",
        url: `geenius-native://${id}`,
        createdAt: new Date().toISOString(),
        blobKey,
      };
      try {
        const files = await readFiles();
        files.push(storedFile);
        await writeFiles(files);
      } catch (cause) {
        await removeUploadBlob(blobKey);
        throw cause;
      }
      const { blobKey: _blobKey, ...publicFile } = storedFile;
      return publicFile;
    },
    async download(fileId: string) {
      const file = (await readFiles()).find(
        (candidate) => candidate.id === fileId,
      );
      if (!file) {
        throw new StorageError(`File not found: ${fileId}`, "NOT_FOUND");
      }
      let dataUrl: string | null;
      try {
        dataUrl = await storage.getItem(file.blobKey);
      } catch (cause) {
        throw new StorageError(
          "Failed to read React Native file blob",
          "DOWNLOAD_FAILED",
          cause,
        );
      }
      if (!dataUrl) {
        throw new StorageError(`Blob missing for file: ${fileId}`, "NOT_FOUND");
      }
      try {
        return dataUrlToBlob(dataUrl);
      } catch (cause) {
        if (cause instanceof StorageError) {
          throw cause;
        }
        throw new StorageError(
          "Failed to decode React Native file data",
          "DOWNLOAD_FAILED",
          cause,
        );
      }
    },
    async delete(fileId: string) {
      const files = await readFiles();
      const file = files.find((candidate) => candidate.id === fileId);
      if (!file) return false;
      try {
        await storage.removeItem(file.blobKey);
      } catch (cause) {
        throw new StorageError(
          "Failed to delete React Native file blob",
          "SERVICE_ERROR",
          cause,
        );
      }
      await writeFiles(files.filter((candidate) => candidate.id !== fileId));
      return true;
    },
    async list(prefix?: string) {
      const files = await readFiles();
      return files
        .filter((file) => !prefix || file.path.startsWith(prefix))
        .map(({ blobKey: _blobKey, ...file }) => file);
    },
    async getUrl(fileId: string) {
      const file = (await readFiles()).find(
        (candidate) => candidate.id === fileId,
      );
      if (!file) {
        throw new StorageError(`File not found: ${fileId}`, "NOT_FOUND");
      }
      try {
        return (await storage.getItem(file.blobKey)) ?? file.url;
      } catch (cause) {
        throw new StorageError(
          "Failed to read React Native file blob",
          "DOWNLOAD_FAILED",
          cause,
        );
      }
    },
  } satisfies FileStorageAdapter;
}

function createNativeAdminAdapter(
  storage: ReactNativeAsyncStorage,
  namespace: string,
): AdminAdapter {
  const usersKey = storageKey(namespace, "auth:users");
  const rolesKey = storageKey(namespace, "admin:roles");
  const teamsKey = storageKey(namespace, "admin:teams");
  const readUsers = () =>
    readJsonArray(
      storage,
      usersKey,
      isStoredNativeUser,
      (cause) =>
        new AdminError(
          "Failed to read React Native admin users",
          "OPERATION_FAILED",
          cause,
        ),
    );
  const writeUsers = (users: StoredNativeUser[]) =>
    writeJson(
      storage,
      usersKey,
      users,
      (cause) =>
        new AdminError(
          "Failed to write React Native admin users",
          "OPERATION_FAILED",
          cause,
        ),
    );
  const readRoles = () =>
    readJsonArray(
      storage,
      rolesKey,
      isStoredNativeAdminRole,
      (cause) =>
        new AdminError(
          "Failed to read React Native admin roles",
          "OPERATION_FAILED",
          cause,
        ),
    );
  const writeRoles = (roles: StoredNativeAdminRole[]) =>
    writeJson(
      storage,
      rolesKey,
      roles,
      (cause) =>
        new AdminError(
          "Failed to write React Native admin roles",
          "OPERATION_FAILED",
          cause,
        ),
    );
  const readTeams = () =>
    readJsonArray(
      storage,
      teamsKey,
      isStoredNativeAdminTeam,
      (cause) =>
        new AdminError(
          "Failed to read React Native admin teams",
          "OPERATION_FAILED",
          cause,
        ),
    );
  const writeTeams = (teams: StoredNativeAdminTeam[]) =>
    writeJson(
      storage,
      teamsKey,
      teams,
      (cause) =>
        new AdminError(
          "Failed to write React Native admin teams",
          "OPERATION_FAILED",
          cause,
        ),
    );

  return {
    async listUsers(options?: AdminListOptions) {
      return applyAdminListOptions(
        (await readUsers()).map(managedUser),
        options,
        (user) => [user.email, user.name, user.plan],
      );
    },
    async getUser(id: string) {
      const user = (await readUsers()).find((candidate) => candidate.id === id);
      return user ? managedUser(user) : null;
    },
    async banUser(id: string) {
      const users = await readUsers();
      const user = users.find((candidate) => candidate.id === id);
      if (!user || user.isBanned) return false;
      user.isBanned = true;
      await writeUsers(users);
      return true;
    },
    async unbanUser(id: string) {
      const users = await readUsers();
      const user = users.find((candidate) => candidate.id === id);
      if (!user?.isBanned) return false;
      user.isBanned = false;
      await writeUsers(users);
      return true;
    },
    async deleteUser(id: string) {
      const users = await readUsers();
      const nextUsers = users.filter((candidate) => candidate.id !== id);
      if (nextUsers.length === users.length) return false;
      await writeUsers(nextUsers);
      await writeTeams(
        (await readTeams()).map((team) => ({
          ...team,
          memberUserIds: team.memberUserIds.filter((userId) => userId !== id),
        })),
      );
      return true;
    },
    async listRoles(options?: AdminListOptions) {
      return applyAdminListOptions(await readRoles(), options, (role) => [
        role.name,
        role.description,
        role.permissions.join(" "),
      ]);
    },
    async getRole(id: string) {
      return (await readRoles()).find((role) => role.id === id) ?? null;
    },
    async upsertRole(input: AdminRoleInput) {
      const roles = await readRoles();
      const existing = input.id
        ? roles.find((role) => role.id === input.id)
        : undefined;
      const role = makeNativeAdminRole(input, existing);
      await writeRoles(upsertById(roles, role));
      return role;
    },
    async deleteRole(id: string) {
      const roles = await readRoles();
      const nextRoles = roles.filter((role) => role.id !== id);
      if (nextRoles.length === roles.length) return false;

      await writeRoles(nextRoles);
      await writeUsers(
        (await readUsers()).map((user) => ({
          ...user,
          roleIds: (user.roleIds ?? []).filter((roleId) => roleId !== id),
        })),
      );
      await writeTeams(
        (await readTeams()).map((team) => ({
          ...team,
          roleIds: team.roleIds.filter((roleId) => roleId !== id),
        })),
      );
      return true;
    },
    async assignUserRole(userId: string, roleId: string) {
      if (!(await readRoles()).some((role) => role.id === roleId)) {
        return false;
      }

      const users = await readUsers();
      const user = users.find((candidate) => candidate.id === userId);
      if (!user) return false;

      const roleIds = uniqueStrings(user.roleIds ?? []);
      if (roleIds.includes(roleId)) return false;

      user.roleIds = [...roleIds, roleId];
      await writeUsers(users);
      return true;
    },
    async removeUserRole(userId: string, roleId: string) {
      const users = await readUsers();
      const user = users.find((candidate) => candidate.id === userId);
      if (!user?.roleIds?.includes(roleId)) return false;

      user.roleIds = user.roleIds.filter(
        (assignedRoleId) => assignedRoleId !== roleId,
      );
      await writeUsers(users);
      return true;
    },
    async listTeams(options?: AdminListOptions) {
      return applyAdminListOptions(await readTeams(), options, (team) => [
        team.name,
        team.description,
      ]);
    },
    async getTeam(id: string) {
      return (await readTeams()).find((team) => team.id === id) ?? null;
    },
    async upsertTeam(input: AdminTeamInput) {
      const teams = await readTeams();
      const existing = input.id
        ? teams.find((team) => team.id === input.id)
        : undefined;
      const team = makeNativeAdminTeam(input, existing);
      await writeTeams(upsertById(teams, team));

      const memberIds = new Set(team.memberUserIds);
      await writeUsers(
        (await readUsers()).map((user) => {
          const teamIds = uniqueStrings(user.teamIds ?? []);
          const shouldHaveTeam = memberIds.has(user.id);
          return {
            ...user,
            teamIds: shouldHaveTeam
              ? uniqueStrings([...teamIds, team.id])
              : teamIds.filter((teamId) => teamId !== team.id),
          };
        }),
      );
      return team;
    },
    async deleteTeam(id: string) {
      const teams = await readTeams();
      const nextTeams = teams.filter((team) => team.id !== id);
      if (nextTeams.length === teams.length) return false;

      await writeTeams(nextTeams);
      await writeUsers(
        (await readUsers()).map((user) => ({
          ...user,
          teamIds: (user.teamIds ?? []).filter((teamId) => teamId !== id),
        })),
      );
      return true;
    },
    async addTeamMember(teamId: string, userId: string) {
      const users = await readUsers();
      const user = users.find((candidate) => candidate.id === userId);
      if (!user) return false;

      const teams = await readTeams();
      const team = teams.find((candidate) => candidate.id === teamId);
      if (!team || team.memberUserIds.includes(userId)) return false;

      team.memberUserIds = [...team.memberUserIds, userId];
      user.teamIds = uniqueStrings([...(user.teamIds ?? []), teamId]);
      await writeTeams(teams);
      await writeUsers(users);
      return true;
    },
    async removeTeamMember(teamId: string, userId: string) {
      const teams = await readTeams();
      const team = teams.find((candidate) => candidate.id === teamId);
      if (!team?.memberUserIds.includes(userId)) return false;

      const users = await readUsers();
      const user = users.find((candidate) => candidate.id === userId);
      team.memberUserIds = team.memberUserIds.filter(
        (memberId) => memberId !== userId,
      );
      if (user?.teamIds) {
        user.teamIds = user.teamIds.filter(
          (assignedTeamId) => assignedTeamId !== teamId,
        );
      }
      await writeTeams(teams);
      await writeUsers(users);
      return true;
    },
    async assignTeamRole(teamId: string, roleId: string) {
      if (!(await readRoles()).some((role) => role.id === roleId)) {
        return false;
      }

      const teams = await readTeams();
      const team = teams.find((candidate) => candidate.id === teamId);
      if (!team || team.roleIds.includes(roleId)) return false;

      team.roleIds = [...team.roleIds, roleId];
      await writeTeams(teams);
      return true;
    },
    async removeTeamRole(teamId: string, roleId: string) {
      const teams = await readTeams();
      const team = teams.find((candidate) => candidate.id === teamId);
      if (!team?.roleIds.includes(roleId)) return false;

      team.roleIds = team.roleIds.filter(
        (assignedRoleId) => assignedRoleId !== roleId,
      );
      await writeTeams(teams);
      return true;
    },
    async getMetrics(): Promise<AdminMetrics> {
      const users = (await readUsers()).map(managedUser);
      const today = new Date().toISOString().slice(0, 10);
      return {
        totalUsers: users.length,
        activeUsers: users.filter((user) => user.isActive && !user.isBanned)
          .length,
        newUsersToday: users.filter((user) => user.createdAt?.startsWith(today))
          .length,
      };
    },
  } satisfies AdminAdapter;
}

export function createReactNativeDefaultAdapterSet(
  options: ReactNativeDefaultAdapterOptions = {},
): ReactNativeDefaultAdapterSet {
  const storage = options.asyncStorage ?? createMemoryStorage();
  const namespace = options.namespace ?? "geenius:adapters:react-native";
  const fileStorage = createNativeFileStorageAdapter(
    storage,
    namespace,
    options.maxUploadBytes ?? DEFAULT_MAX_UPLOAD_BYTES,
  );
  const providerName = options.asyncStorage ? "asyncStorage" : "memory";
  const logger = createConsoleLoggerAdapter();

  if (isProductionRuntime(options.isProduction)) {
    void logger.warn(
      "React Native default adapters are local/dev mocks. Do NOT use them in production; wire production providers in app adapter modules.",
      {
        package: "@geenius/adapters/react-native",
        provider: providerName,
      },
    );
  }

  return {
    providerName,
    adapters: {
      db: createNativeDbAdapter(storage, namespace),
      auth: createNativeAuthAdapter(storage, namespace),
      payments: createNativePaymentsAdapter(storage, namespace),
      ai: createNativeAiAdapter(storage, namespace, options.aiRateLimit),
      storage: fileStorage,
      fileStorage,
      admin: createNativeAdminAdapter(storage, namespace),
      logger,
    },
  };
}

export const REACT_NATIVE_DEFAULT_PROVIDER_BY_DOMAIN: Record<
  AdapterDomain,
  string
> = {
  db: "memory",
  auth: "memory",
  payments: "memory",
  ai: "memory",
  storage: "memory",
  admin: "memory",
  logger: "console",
};
